import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ChevronRight20Regular, Dismiss20Regular, Folder24Regular, FolderAdd20Regular, FolderProhibited24Regular, Search20Regular } from "@fluentui/react-icons";
import type { DriveFolders, FolderItem, TemplateNode } from "@app/sp-data";
import { FolderExistsError } from "@app/sp-data";
import { eCartellaDiGara } from "../data/cartelle";
import { describeError } from "./ErrorState";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  bar: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap", alignItems: "center" },
  search: { flexGrow: 1, minWidth: "200px" },
  list: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minHeight: "48px",
    padding: `0 ${tokens.spacingHorizontalM}`,
    border: "none",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  rowDisabled: { cursor: "default", color: tokens.colorNeutralForeground3, ":hover": { backgroundColor: "transparent" } },
  icon: { color: tokens.colorBrandForeground1, flexShrink: 0 },
  iconOff: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  name: { flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" },
  path: { display: "block", color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  empty: { padding: tokens.spacingHorizontalL, color: tokens.colorNeutralForeground3 },
  nuova: { display: "flex", gap: tokens.spacingHorizontalS, alignItems: "flex-end", flexWrap: "wrap" },
});

export interface PickerState {
  current: FolderItem;
  /** Motivo per cui la cartella corrente non è una destinazione valida. */
  invalid?: string;
}

/**
 * Selettore di cartelle di una raccolta: navigazione a livelli (nessun limite di righe),
 * percorso, ricerca per nome, creazione di cartelle intermedie.
 * Le cartelle di gara si vedono ma non si aprono e non sono destinazioni valide.
 */
export function FolderPicker(props: {
  folders: DriveFolders;
  root: FolderItem;
  modello?: TemplateNode[];
  onChange: (s: PickerState) => void;
}) {
  const s = useStyles();
  const [stack, setStack] = useState<FolderItem[]>([props.root]);
  const current = stack[stack.length - 1]!;
  const [children, setChildren] = useState<FolderItem[]>();
  const [error, setError] = useState<unknown>();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FolderItem[]>();
  const [searching, setSearching] = useState(false);
  const [nuova, setNuova] = useState<string>();
  const [nuovaErr, setNuovaErr] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function load(folder: FolderItem) {
    setChildren(undefined);
    setError(undefined);
    try {
      setChildren((await props.folders.children(folder.id)).folders);
    } catch (e) {
      setError(e);
    }
  }

  useEffect(() => {
    void load(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id]);

  const currentIsGara = useMemo(
    () => current.id !== props.root.id && !!children && eCartellaDiGara(current, props.modello, children),
    [current, children, props.modello, props.root.id],
  );

  useEffect(() => {
    const invalid =
      current.id === props.root.id
        ? "Scegli almeno il tipo di ente (es. Comuni, Asl)."
        : currentIsGara
          ? "Questa è una cartella di gara: scegli la cartella che la contiene."
          : undefined;
    props.onChange({ current, invalid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id, currentIsGara]);

  const open = (f: FolderItem) => {
    setQuery("");
    setResults(undefined);
    setStack((st) => [...st, f]);
  };

  /** Apre una cartella trovata con la ricerca, ricostruendo il percorso dalla radice. */
  async function openPath(f: FolderItem) {
    setBusy(true);
    try {
      const segs = f.path.split("/");
      const chain: FolderItem[] = [props.root];
      for (let i = 1; i < segs.length; i++) chain.push(await props.folders.byPath(segs.slice(0, i).join("/")));
      chain.push(f);
      setQuery("");
      setResults(undefined);
      setStack(chain);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function search() {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setError(undefined);
    try {
      setResults(await props.folders.search(q));
    } catch (e) {
      setError(e);
    } finally {
      setSearching(false);
    }
  }

  async function creaIntermedia() {
    if (!nuova?.trim()) return;
    setBusy(true);
    setNuovaErr(undefined);
    try {
      const f = await props.folders.create(current.id, nuova);
      setNuova(undefined);
      open(f);
    } catch (e) {
      setNuovaErr(e instanceof FolderExistsError ? e.message : describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  const rows = results ?? children;

  return (
    <div className={s.root}>
      <Breadcrumb aria-label="Percorso" size="medium">
        {stack.map((f, i) => (
          <Fragment key={f.id}>
            {i > 0 && <BreadcrumbDivider />}
            <BreadcrumbItem>
              <BreadcrumbButton current={i === stack.length - 1} onClick={() => setStack(stack.slice(0, i + 1))}>
                {i === 0 ? "Repository" : f.name}
              </BreadcrumbButton>
            </BreadcrumbItem>
          </Fragment>
        ))}
      </Breadcrumb>

      <div className={s.bar}>
        <Input
          className={s.search}
          contentBefore={<Search20Regular />}
          contentAfter={
            query ? (
              <Button
                size="small"
                appearance="transparent"
                icon={<Dismiss20Regular />}
                aria-label="Cancella ricerca"
                onClick={() => {
                  setQuery("");
                  setResults(undefined);
                }}
              />
            ) : undefined
          }
          placeholder="Cerca una cartella (es. il nome del Comune)"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          onKeyDown={(e) => e.key === "Enter" && void search()}
        />
        <Button onClick={search} disabled={query.trim().length < 2 || searching}>
          Cerca
        </Button>
        {!results && !currentIsGara && (
          <Button icon={<FolderAdd20Regular />} onClick={() => setNuova(nuova === undefined ? "" : undefined)}>
            Nuova cartella
          </Button>
        )}
      </div>

      {nuova !== undefined && (
        <div className={s.nuova}>
          <Input
            autoFocus
            style={{ flexGrow: 1 }}
            placeholder={`Nuova cartella in ${current.path || "Repository"}`}
            value={nuova}
            onChange={(_, d) => setNuova(d.value)}
            onKeyDown={(e) => e.key === "Enter" && void creaIntermedia()}
          />
          <Button appearance="primary" onClick={creaIntermedia} disabled={busy || !nuova.trim()}>
            Crea
          </Button>
          <Button onClick={() => setNuova(undefined)}>Annulla</Button>
          {nuovaErr && (
            <MessageBar intent="error" style={{ width: "100%" }}>
              <MessageBarBody>{nuovaErr}</MessageBarBody>
            </MessageBar>
          )}
        </div>
      )}

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{describeError(error).detail}</MessageBarBody>
        </MessageBar>
      ) : null}

      {results && <Caption1>{results.length ? `Risultati per "${query}"` : `Nessuna cartella trovata per "${query}".`}</Caption1>}

      {currentIsGara ? (
        <MessageBar intent="warning">
          <MessageBarBody>"{current.name}" è una cartella di gara. Torna indietro e scegli la cartella dell'ente.</MessageBarBody>
        </MessageBar>
      ) : !rows || busy || searching ? (
        <Spinner size="small" style={{ padding: 24 }} />
      ) : (
        <div className={s.list} role="list">
          {rows.length === 0 && !results ? (
            <div className={s.empty}>Nessuna sottocartella. Puoi creare qui la cartella di gara, oppure una nuova cartella.</div>
          ) : (
            rows.map((f) => {
              const gara = eCartellaDiGara(f);
              return (
                <button
                  key={f.id}
                  type="button"
                  role="listitem"
                  className={mergeClasses(s.row, gara && s.rowDisabled)}
                  onClick={() => (gara ? undefined : results ? void openPath(f) : open(f))}
                  aria-disabled={gara}
                >
                  {gara ? <FolderProhibited24Regular className={s.iconOff} /> : <Folder24Regular className={s.icon} />}
                  <span className={s.name}>
                    {f.name}
                    {results && <span className={s.path}>{f.path}</span>}
                  </span>
                  {gara ? (
                    <Badge appearance="tint" color="informative" shape="rounded">
                      Cartella di gara
                    </Badge>
                  ) : (
                    <ChevronRight20Regular />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
